import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { USER_MGMT_CNST } from '../../../../constants/proj.cnst';
import { UM_TABLE_CNST } from '../../../../constants/table.cnst';
import { IDepartmentOption, IUserSummaryCard } from '../../../../interfaces/list-users.config';
import { AppStateService } from '../../../../services/app-state.service';
import { ConfirmationService } from 'primeng/api';
import { ImportsModule } from '../../../../modules/imports/imports';
import { PrimengTableComponent } from '../../../../modules/shared/components/primeng-table/primeng-table.component';
import { TruncatePipe } from '../../../../pipes/truncate.pipe';
import { SkeletonModule } from '../../../../modules/shared/components/skeleton/skeleton.module';
import { TablerIconComponent } from 'angular-tabler-icons';
import { buildDepartmentOptions, mapUsersWithDetails } from './user-details.mapper';


@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
  standalone : true,
  imports : [
    CommonModule,
    ImportsModule,
    PrimengTableComponent,
    TruncatePipe,
    SkeletonModule,
    TablerIconComponent,
  ]
})

export class UsersListComponent implements OnChanges {
  @Input() tableConfig: any = {};
  @Input() tableData: any = [];
  @Input() isCustomer: any = false;
  @Input() isEditPermission: boolean = false;

  loading: boolean = false;
  userSummaryCards: IUserSummaryCard[] = [];
  usersWithDepartment: any[] = [];
  displayedTableData: any[] = [];
  departmentOptions: IDepartmentOption[] = buildDepartmentOptions([]);
  selectedDepartments: string[] = [];
  departmentFilterPlaceholder = USER_MGMT_CNST.DEPARTMENT_FILTER_PLACEHOLDER;


  dataTypes = UM_TABLE_CNST.CLICKABLE_DATA_TYPE;
  opts = USER_MGMT_CNST.OPTIONS;
  showUserId : boolean = false;
  showDeletePopup: boolean = false;
  rowData: any;
  constructor(
    private appStateSrv: AppStateService,
    private router: Router,
    private confirmationService: ConfirmationService,
  ) {
    this.applyDepartmentFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData']) {
      const previousDepartmentOptions = this.departmentOptions;
      this.usersWithDepartment = mapUsersWithDetails(this.tableData);
      this.departmentOptions = buildDepartmentOptions(this.usersWithDepartment);
      this.syncSelectedDepartments(previousDepartmentOptions);
      this.applyDepartmentFilter();
    }
  }

  onDepartmentChange(event: any): void {
    const selectedValues = Array.isArray(event?.value) ? [...event.value] : [];
    this.selectedDepartments = selectedValues;
    this.applyDepartmentFilter();
  }

  private syncSelectedDepartments(previousDepartmentOptions: IDepartmentOption[]): void {
    const availableValues = this.getDepartmentValues();

    if (!previousDepartmentOptions.length) {
      this.selectedDepartments = [...availableValues];
      return;
    }

    const previousValues = previousDepartmentOptions.map((option) => option.value);
    const allDepartmentsWereSelected = previousValues.length > 0
      && previousValues.every((value) => this.selectedDepartments.includes(value));

    if (allDepartmentsWereSelected) {
      this.selectedDepartments = [...availableValues];
      return;
    }

    const availableValuesSet = new Set(availableValues);
    this.selectedDepartments = this.selectedDepartments.filter((value) => availableValuesSet.has(value));
  }

  private applyDepartmentFilter(): void {
    this.displayedTableData = this.shouldShowAllDepartments()
      ? [...this.usersWithDepartment]
      : this.usersWithDepartment.filter((user) => this.selectedDepartments.includes(user.department));
    this.userSummaryCards = this.buildUserSummaryCards();
  }

  private shouldShowAllDepartments(): boolean {
    const departmentValues = this.getDepartmentValues();
    if (!departmentValues.length) {
      return true;
    }

    return departmentValues.every((value) => this.selectedDepartments.includes(value));
  }

  private getDepartmentValues(): string[] {
    return this.departmentOptions.map((option) => option.value);
  }

  private buildUserSummaryCards(): IUserSummaryCard[] {
    const summaryValues = {
      departmentWiseCount: this.getDepartmentWiseUsersCount(),
      totalUsers: this.getTotalUsersCount(),
      activeUsersLast24Hrs: this.getActiveUsersLast24HoursCount(),
      activeUsersLast7Days: this.getActiveUsersLast7DaysCount(),
      neverLoggedIn: this.getNeverLoggedInUsersCount()
    };

    return USER_MGMT_CNST.USER_SUMMARY_CARD_CONFIG.map((cardConfig) => ({
      key: cardConfig.key,
      label: cardConfig.label,
      subtext: cardConfig.subtext,
      icon: cardConfig.icon,
      iconClass: cardConfig.iconClass,
      value: cardConfig.isPlaceholder
        ? USER_MGMT_CNST.USER_SUMMARY_PLACEHOLDER
        : summaryValues[cardConfig.key as keyof typeof summaryValues]
    }));
  }

  private getTotalUsersCount(): number {
    return this.getUsersCount(this.usersWithDepartment);
  }

  private getDepartmentWiseUsersCount(): number {
    return this.getUsersCount(this.displayedTableData);
  }

  private getActiveUsersLast24HoursCount(): number {
    return this.getUsersCountByPredicate((user) => this.hasPositiveLoginCount(user?.loginCount24h));
  }

  private getActiveUsersLast7DaysCount(): number {
    return this.getUsersCountByPredicate((user) => this.hasPositiveLoginCount(user?.loginCount7d));
  }

  private getNeverLoggedInUsersCount(): number {
    return this.getUsersCountByPredicate((user) => this.hasNeverLoggedIn(user));
  }

  private getUsersCount(users: any[]): number {
    return Array.isArray(users) ? users.length : 0;
  }

  private getUsersCountByPredicate(predicate: (user: any) => boolean): number {
    if (!Array.isArray(this.displayedTableData)) {
      return 0;
    }

    return this.displayedTableData.filter(predicate).length;
  }

  private hasPositiveLoginCount(loginCount: any): boolean {
    const parsedLoginCount = Number(loginCount);
    return !Number.isNaN(parsedLoginCount) && parsedLoginCount > 0;
  }

  private hasNeverLoggedIn(user: any): boolean {
    return user?.neverLoggedIn === true || user?.neverLoggedIn === 'true';
  }

  handleOptionClick(rowData: any, operation: string) {  
    if ((operation === USER_MGMT_CNST.OPTIONS.EDIT || operation === USER_MGMT_CNST.OPTIONS.DELETE) && !this.isEditPermission) {
      return;
    }
    this.rowData = rowData; 
    let data = rowData;
    if(operation == USER_MGMT_CNST.OPTIONS.DELETE) {
      this.confirmationService.confirm({
        // target: event.target as EventTarget,
        message: 'Do you want to delete this user?',
        header: 'Confirmation',
        // rejectButtonStyleClass: "p-button-text",
        rejectButtonProps: {
          label: 'Cancel',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Delete',
          severity: 'danger',
          // size:'large'
        },
        accept: () => {
          this.protectedToDeleteOrNot(true);
        },
        reject: () => {
        }
      });
    }else {
      this.router.navigate(['usermgmt','users','config'], {
        queryParams: {
          operation: operation.toLowerCase(),
          userId: data.userId,
          config: 'info',
          department: data?.department,
          division: data?.division
        }
      })
    }
  }

  protectedToDeleteOrNot(option: boolean) {
    if(!option) {
      this.showDeletePopup = false;
    }
    else {
      const postData = {
        userId: this.rowData.userId
      }
      this.setOperationType({
        operation: USER_MGMT_CNST.OPERATION_TYPES.USER.DELETE_USER,
        postData
      });
    }
  }

  setOperationType(value : any) {
    this.appStateSrv.operationRef.next(value);
  }


  async setTableData(size : any = null) {
      this.loading = true;
  
      // let options = {
      //   'queryParams': {
      //     size:  isNullOrUndefined(size) ? USER_MGMT_CNST.INFINITE_SCROLL_OPTIONS.INFINITE_SCROLL_LIMIT : size,
      //     offset: this.currentOffset
      //   }
      // }
  
      // let profilesListApiResponse = await this.commonSrv.getProfilesList(options);
      // let responseKeys = Object.keys(profilesListApiResponse);
  
      this.loading = false;
  
      // if (!this.isCustomerListFetched) {
      //   this.setCustomerListData();
      // }
  
      // if (responseKeys.length > 0) {
      //   let tableData = this.formatTableData(profilesListApiResponse);
  
      //   // * If page was scrolled and edit/add/delete was not performed
      //   if (this.wasPageScrolled && isNullOrUndefined(this.currentOperation)) {
      //     this.tableData = [...this.tableData, ...tableData];
      //   } else if (this.currentOffset === 0) {
      //       this.tableData = tableData;
      //     }
  
        // this.totalRecordCount = this.currentOffset === 0
        //   ? tableData.length
        //   : this.totalRecordCount + tableData.length;
  
        // this.currentOperation = null;
  
        // if (!isNullOrUndefined(size)) {
        //   this.currentOffset = this.totalRecordCount + 1;
        // }
      }
    }


